/**
 * lib/clarity.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Microsoft Clarity session linkage.
 *
 * Goal: every error report carries a stable session ID that we also pass to
 * Clarity (via `clarity('identify', userId, sessionId)`). The admin error log
 * surfaces that ID so we can find the matching Clarity session replay and
 * literally watch what the user did right before the crash.
 *
 * IMPORTANT: this module is defensive — clarity.js may not have loaded yet
 * (or at all, in dev). Calls made before it arrives are queued on the same
 * stub the official snippet installs and replayed on load; every call is
 * wrapped so it never throws. The session ID is generated and stored
 * regardless; if Clarity never loads, the ID still flows into error reports
 * and lets us group "all crashes from one tab" without the replay.
 *
 * The session ID lifecycle is per-tab: stored in `sessionStorage`, regenerated
 * when the user opens a new tab. Persists across reloads of the same tab.
 */

const STORAGE_KEY = 'obi-clarity-session-id'

/**
 * Shape of `window.clarity`. Before clarity.js loads it is the official
 * queue stub (a function that records calls on `.q`); once loaded, the tag
 * replaces it with the real API and replays the queue.
 */
type ClarityFn = ((action: string, ...args: unknown[]) => void) & { q?: unknown[][] }

declare global {
  interface Window {
    /**
     * Microsoft Clarity's global function. Installed by the inline snippet in
     * app/layout.tsx (or by `clarity()` below, whichever runs first).
     */
    clarity?: ClarityFn
  }
}

/**
 * Returns `window.clarity`, installing the same queue stub the official
 * snippet uses if neither it nor clarity.js has run yet. The Clarity tag is
 * loaded `afterInteractive` (it competes with hydration otherwise), so the
 * identify call from AppLayoutClient's mount effect can legitimately run
 * BEFORE the snippet — without the stub that call would be a silent no-op and
 * the error-log ↔ session-replay link would be lost for the whole tab. The
 * snippet keeps an existing `window.clarity` (`c[a]=c[a]||…`) and clarity.js
 * drains `.q` on load by applying each queued argument list, so anything
 * queued here is delivered. (The snippet pushes `arguments` objects; we push
 * arrays — both are array-likes to `apply`.)
 */
function clarity(): ClarityFn | null {
  if (typeof window === 'undefined') return null
  if (!window.clarity) {
    const stub: ClarityFn = (...args: unknown[]) => {
      (stub.q = stub.q || []).push(args)
    }
    window.clarity = stub
  }
  return window.clarity
}

/**
 * Crypto-strong UUID generator. Falls back to Math.random in environments
 * without crypto.randomUUID (very old Safari, some embedded webviews).
 */
function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback: not cryptographically strong, but good enough as a tab-scoped ID.
  return 'fallback-' +
    Math.random().toString(36).slice(2, 10) +
    '-' +
    Date.now().toString(36)
}

/**
 * Returns the current session ID for this browser tab, generating one on
 * first call. Returns null during SSR.
 */
export function getClaritySessionId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    let id = window.sessionStorage.getItem(STORAGE_KEY)
    if (!id) {
      id = generateSessionId()
      window.sessionStorage.setItem(STORAGE_KEY, id)
    }
    return id
  } catch {
    // sessionStorage can throw in privacy-mode Safari — generate ephemeral.
    return generateSessionId()
  }
}

/**
 * Tell Microsoft Clarity who this user is and tag the session with our own
 * IDs.  Safe to call multiple times; safe to call before clarity.js loads
 * (the call is queued on the stub and replayed once the tag arrives).
 *
 * Also calls `upgrade` so EVERY identified session is preserved in Clarity's
 * retention pool — not just sessions that crash. The reason: by the time
 * reportClientError fires its own upgrade call, the session may already be a
 * write-off. Calling upgrade on identify means any authenticated session is
 * priority-retained regardless of what happens later.
 *
 * Call once from AppLayoutClient after the user is known.
 */
export function identifyClarity(userId: string | null): void {
  if (typeof window === 'undefined') return
  const sessionId = getClaritySessionId()
  if (!sessionId) return
  try {
    // Microsoft Clarity identify API:
    //   clarity('identify', customId, customSessionId, customPageId, friendlyName)
    // The custom-session-id we pass is what shows up as the "Custom Session ID"
    // filter in the Clarity dashboard, which is how the admin error log links
    // an error back to its session replay.
    clarity()?.('identify', userId ?? 'anonymous', sessionId)
    // Mark every authenticated session as priority-retained. Defensive: we'd
    // rather over-retain than miss the next crash session.
    clarity()?.('upgrade', 'authenticated_session')
  } catch {
    // Never let analytics break the app.
  }
}

/**
 * Mark the current Clarity session as "high-priority for storage".  Microsoft
 * Clarity records every session by default but evicts older ones when the
 * project hits its retention cap; the `upgrade` API tells Clarity to keep
 * this one. Call this from the error reporter so any session that produced
 * a crash is preserved long enough for us to watch.
 *
 * Reason string is shown in the Clarity dashboard.
 */
export function upgradeClaritySession(reason: string): void {
  if (typeof window === 'undefined') return
  try {
    clarity()?.('upgrade', reason)
  } catch {
    // Never let analytics break the app.
  }
}
